import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { useAuth } from '../contexts/AuthContext';
import { loadAuthSession } from '../utils/authStorage';
import { API_URL } from '../utils/config';
import './CaseDetails.css';

interface CaseMetadata {
  case_number: string;
  policy_number?: string | null;
  project_number?: string | null;
  address?: string;
  date_start?: string | null;
  date_end?: string | null;
  status: string;
  created_at?: string;
  updated_at?: string;
  direction?: string;
  structure_type?: string;
  total_floors?: number;
  notes?: string;
}

interface ResourceCounts {
  images?: number;
  svgs?: number;
  audio_files?: number;
  weather_documents?: number;
  ai_analyses?: number;
  weather_data?: number;
  reports?: number;
  logs?: number;
}

interface EvidenceItem {
  id: string;
  media_type: 'image' | 'svg' | 'audio' | 'weather_document';
  type: string;
  filename: string;
  caption?: string;
  floor_level?: number | null;
  is_svg_image?: boolean;
  svg_data?: string;
  transcript?: string;
  file_size?: number;
  file_type?: string;
  summary?: string;
  url: string;
  created_at?: string;
}

interface WeatherItem {
  id?: string;
  weather_date: string;
  weather_json?: {
    temperature?: number | string;
    wind_speed?: number | string;
    wind_direction?: string;
    precipitation?: number | string;
    humidity?: number | string;
    description?: string;
    [key: string]: any;
  };
  created_at?: string;
}

interface AIAnalysisItem {
  id?: string;
  image_id?: string;
  svg_id?: string;
  short_description?: string;
  analysis_json?: Record<string, any>;
  created_at?: string;
}

interface LogItem {
  id?: string;
  stage?: string;
  input?: string;
  output?: string;
  created_at?: string;
}

interface ReportItem {
  id?: string;
  filename: string;
  created_at?: string;
  docx_metadata?: Record<string, any>;
}

type TabType = 'evidence' | 'weather' | 'analysis' | 'reports' | 'logs';
type EvidenceFilter = 'all' | 'photograph' | 'svg' | 'audio' | 'weather_document';

const mediaBlobCache = new Map<string, string>();

/**
 * Fetches media with the Authorization header and renders it via an object URL.
 * Plain <img>/<audio>/<a> tags cannot send the Bearer token the media endpoint
 * requires, so cross-origin evidence requests would otherwise 401.
 */
const AuthImage: React.FC<{
  src: string;
  alt?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  onError?: () => void;
}> = ({ src, alt, className, loading, onError }) => {
  const { getAuthHeaders } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(() => mediaBlobCache.get(src) ?? null);

  useEffect(() => {
    let revoked: string | null = null;
    let cancelled = false;
    const cached = mediaBlobCache.get(src);
    if (cached) {
      setBlobUrl(cached);
      return;
    }
    setBlobUrl(null);
    fetch(src, { headers: { ...getAuthHeaders() } })
      .then((res) => {
        if (!res.ok) throw new Error(`Media request failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        mediaBlobCache.set(src, url);
        revoked = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) onError?.();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!blobUrl) return <div className={`auth-media-loading ${className ?? ''}`} />;
  return <img src={blobUrl} alt={alt} className={className} loading={loading} onError={onError} />;
};

const AuthAudio: React.FC<{ src: string }> = ({ src }) => {
  const { getAuthHeaders } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(() => mediaBlobCache.get(src) ?? null);

  useEffect(() => {
    let cancelled = false;
    const cached = mediaBlobCache.get(src);
    if (cached) {
      setBlobUrl(cached);
      return;
    }
    setBlobUrl(null);
    fetch(src, { headers: { ...getAuthHeaders() } })
      .then((res) => {
        if (!res.ok) throw new Error(`Media request failed (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        mediaBlobCache.set(src, url);
        setBlobUrl(url);
      })
      .catch(() => setBlobUrl(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  if (!blobUrl) return <div className="auth-media-loading" />;
  return (
    <audio controls preload="metadata" className="custom-audio-player">
      <source src={blobUrl} type="audio/mpeg" />
      Your browser does not support audio playback.
    </audio>
  );
};

const openAuthPdf = async (src: string, _filename: string) => {
  // Open the tab synchronously inside the click handler so popup blockers
  // don't swallow it, then navigate it to the blob once fetched.
  const tab = window.open('', '_blank');
  try {
    const headers: Record<string, string> = {};
    const stored = loadAuthSession();
    if (stored?.accessToken) headers['Authorization'] = `Bearer ${stored.accessToken}`;
    const res = await fetch(src, { headers });
    if (!res.ok) throw new Error(`Failed to open document (${res.status})`);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    if (tab) {
      tab.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    tab?.close();
    alert(err instanceof Error ? err.message : 'Failed to open document.');
  }
};

const CaseDetails: React.FC = () => {
  const { caseNumber } = useParams<{ caseNumber: string }>();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  // Primary Case Overview State
  const [caseData, setCaseData] = useState<CaseMetadata | null>(null);
  const [counts, setCounts] = useState<ResourceCounts>({});
  const [reviewState, setReviewState] = useState<{ status: string; draft_available: boolean; draft_updated_at?: string } | null>(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [initialError, setInitialError] = useState<string | null>(null);

  // Active Tab State
  const [activeTab, setActiveTab] = useState<TabType>('evidence');
  const [evidenceFilter, setEvidenceFilter] = useState<EvidenceFilter>('all');

  // Evidence Tab Infinite Scroll State (Offset Pagination)
  const [evidenceItems, setEvidenceItems] = useState<EvidenceItem[]>([]);
  const [evidenceHasMore, setEvidenceHasMore] = useState(true);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  // Weather Tab Infinite Scroll State (Offset Pagination)
  const [weatherItems, setWeatherItems] = useState<WeatherItem[]>([]);
  const [weatherHasMore, setWeatherHasMore] = useState(true);
  const [weatherLoading, setWeatherLoading] = useState(false);

  // AI Analysis Tab Infinite Scroll State (Offset Pagination)
  const [analysisItems, setAnalysisItems] = useState<AIAnalysisItem[]>([]);
  const [analysisHasMore, setAnalysisHasMore] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  // Logs Tab Infinite Scroll State (Offset Pagination)
  const [logItems, setLogItems] = useState<LogItem[]>([]);
  const [logHasMore, setLogHasMore] = useState(true);
  const [logLoading, setLogLoading] = useState(false);

  // Reports List State
  const [reportItems, setReportItems] = useState<ReportItem[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState<string | null>(null);

  // Lightbox & Preview Modals
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<EvidenceItem | null>(null);

  // Observer Sentinel Ref & Request Guard Refs
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadedTabsRef = useRef<Record<TabType, boolean>>({
    evidence: false,
    weather: false,
    analysis: false,
    logs: false,
    reports: false,
  });
  const loadingRef = useRef<Record<TabType, boolean>>({
    evidence: false,
    weather: false,
    analysis: false,
    logs: false,
    reports: false,
  });

  // Fetch initial case metadata & counts
  const fetchCaseOverview = useCallback(async () => {
    if (!caseNumber) return;
    try {
      setLoadingInitial(true);
      setInitialError(null);
      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });

      if (res.status === 401) {
        setInitialError('session_expired');
        return;
      }
      if (res.status === 404) {
        setInitialError('Case not found or access denied.');
        return;
      }
      if (!res.ok) {
        throw new Error('Failed to load case details.');
      }

      const data = await res.json();
      setCaseData(data.case);
      setCounts(data.counts || {});
      setReviewState(data.review || null);
      if (data.reports) {
        setReportItems(data.reports);
      }
    } catch (err) {
      setInitialError(err instanceof Error ? err.message : 'An error occurred loading the case.');
    } finally {
      setLoadingInitial(false);
    }
  }, [caseNumber, getAuthHeaders]);

  useEffect(() => {
    fetchCaseOverview();
  }, [fetchCaseOverview]);

  // Fetch Evidence with Offset Pagination
  const fetchEvidenceOffset = useCallback(async (offsetToLoad: number, filterToUse: EvidenceFilter, replace: boolean = false) => {
    if (!caseNumber || loadingRef.current.evidence) return;
    try {
      loadingRef.current.evidence = true;
      setEvidenceLoading(true);
      const params = new URLSearchParams({
        limit: '12',
        offset: String(offsetToLoad),
      });
      if (filterToUse !== 'all') {
        params.set('type', filterToUse);
      }

      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}/evidence?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch evidence');

      const data = await res.json();
      const newItems: EvidenceItem[] = data.items || [];
      const hasMore: boolean = Boolean(data.has_more ?? ((offsetToLoad + newItems.length) < (data.total || 0)));

      setEvidenceItems((prev) => (replace ? newItems : [...prev, ...newItems]));
      setEvidenceHasMore(hasMore);
      loadedTabsRef.current.evidence = true;
    } catch (err) {
      console.error('Error loading evidence:', err);
    } finally {
      loadingRef.current.evidence = false;
      setEvidenceLoading(false);
    }
  }, [caseNumber, getAuthHeaders]);

  // Fetch Weather with Offset Pagination
  const fetchWeatherOffset = useCallback(async (offsetToLoad: number, replace: boolean = false) => {
    if (!caseNumber || loadingRef.current.weather) return;
    try {
      loadingRef.current.weather = true;
      setWeatherLoading(true);
      const params = new URLSearchParams({
        limit: '20',
        offset: String(offsetToLoad),
      });

      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}/weather?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch weather data');

      const data = await res.json();
      const newItems: WeatherItem[] = data.weather_data || [];
      const hasMore: boolean = Boolean(data.has_more ?? ((offsetToLoad + newItems.length) < (data.total || 0)));

      setWeatherItems((prev) => (replace ? newItems : [...prev, ...newItems]));
      setWeatherHasMore(hasMore);
      loadedTabsRef.current.weather = true;
    } catch (err) {
      console.error('Error loading weather data:', err);
    } finally {
      loadingRef.current.weather = false;
      setWeatherLoading(false);
    }
  }, [caseNumber, getAuthHeaders]);

  // Fetch AI Analysis with Offset Pagination
  const fetchAnalysisOffset = useCallback(async (offsetToLoad: number, replace: boolean = false) => {
    if (!caseNumber || loadingRef.current.analysis) return;
    try {
      loadingRef.current.analysis = true;
      setAnalysisLoading(true);
      const params = new URLSearchParams({
        limit: '10',
        offset: String(offsetToLoad),
      });

      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}/analysis?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch analysis');

      const data = await res.json();
      const newItems: AIAnalysisItem[] = data.ai_analyses || [];
      const hasMore: boolean = Boolean(data.has_more ?? ((offsetToLoad + newItems.length) < (data.total || 0)));

      setAnalysisItems((prev) => (replace ? newItems : [...prev, ...newItems]));
      setAnalysisHasMore(hasMore);
      loadedTabsRef.current.analysis = true;
    } catch (err) {
      console.error('Error loading analysis:', err);
    } finally {
      loadingRef.current.analysis = false;
      setAnalysisLoading(false);
    }
  }, [caseNumber, getAuthHeaders]);

  // Fetch Logs with Offset Pagination
  const fetchLogsOffset = useCallback(async (offsetToLoad: number, replace: boolean = false) => {
    if (!caseNumber || loadingRef.current.logs) return;
    try {
      loadingRef.current.logs = true;
      setLogLoading(true);
      const params = new URLSearchParams({
        limit: '20',
        offset: String(offsetToLoad),
      });

      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}/logs?${params}`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error('Failed to fetch logs');

      const data = await res.json();
      const newItems: LogItem[] = data.logs || [];
      const hasMore: boolean = Boolean(data.has_more ?? ((offsetToLoad + newItems.length) < (data.total || 0)));

      setLogItems((prev) => (replace ? newItems : [...prev, ...newItems]));
      setLogHasMore(hasMore);
      loadedTabsRef.current.logs = true;
    } catch (err) {
      console.error('Error loading logs:', err);
    } finally {
      loadingRef.current.logs = false;
      setLogLoading(false);
    }
  }, [caseNumber, getAuthHeaders]);

  // Fetch Reports List
  const fetchReportsList = useCallback(async () => {
    if (!caseNumber || loadingRef.current.reports) return;
    try {
      loadingRef.current.reports = true;
      setReportsLoading(true);
      const res = await fetch(`${API_URL}/api/cases/${encodeURIComponent(caseNumber)}/reports`, {
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
      });
      if (res.ok) {
        const data = await res.json();
        setReportItems(data.reports || []);
      }
      loadedTabsRef.current.reports = true;
    } catch (err) {
      console.error('Error loading reports:', err);
    } finally {
      loadingRef.current.reports = false;
      setReportsLoading(false);
    }
  }, [caseNumber, getAuthHeaders]);

  // Load first offset (0) on tab activation exactly once per tab
  useEffect(() => {
    if (!caseData) return;
    if (activeTab === 'evidence' && !loadedTabsRef.current.evidence) {
      fetchEvidenceOffset(0, evidenceFilter, true);
    } else if (activeTab === 'weather' && !loadedTabsRef.current.weather) {
      fetchWeatherOffset(0, true);
    } else if (activeTab === 'analysis' && !loadedTabsRef.current.analysis) {
      fetchAnalysisOffset(0, true);
    } else if (activeTab === 'logs' && !loadedTabsRef.current.logs) {
      fetchLogsOffset(0, true);
    } else if (activeTab === 'reports' && !loadedTabsRef.current.reports) {
      fetchReportsList();
    }
  }, [activeTab, caseData, evidenceFilter, fetchEvidenceOffset, fetchWeatherOffset, fetchAnalysisOffset, fetchLogsOffset, fetchReportsList]);

  // Evidence filter switch
  const handleEvidenceFilterChange = (filter: EvidenceFilter) => {
    setEvidenceFilter(filter);
    setEvidenceItems([]);
    setEvidenceHasMore(true);
    loadedTabsRef.current.evidence = false;
    fetchEvidenceOffset(0, filter, true);
  };

  // Infinite scroll IntersectionObserver setup using currentItems.length as offset
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          if (activeTab === 'evidence' && evidenceHasMore && !loadingRef.current.evidence) {
            fetchEvidenceOffset(evidenceItems.length, evidenceFilter, false);
          } else if (activeTab === 'weather' && weatherHasMore && !loadingRef.current.weather) {
            fetchWeatherOffset(weatherItems.length, false);
          } else if (activeTab === 'analysis' && analysisHasMore && !loadingRef.current.analysis) {
            fetchAnalysisOffset(analysisItems.length, false);
          } else if (activeTab === 'logs' && logHasMore && !loadingRef.current.logs) {
            fetchLogsOffset(logItems.length, false);
          }
        }
      },
      { threshold: 0.1, rootMargin: '150px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeTab, evidenceHasMore, evidenceItems.length, evidenceFilter, fetchEvidenceOffset, weatherHasMore, weatherItems.length, fetchWeatherOffset, analysisHasMore, analysisItems.length, fetchAnalysisOffset, logHasMore, logItems.length, fetchLogsOffset]);

  // Download Report Action
  const downloadReport = async (filename: string) => {
    if (!caseNumber) return;
    setDownloadingReport(filename);
    try {
      const response = await fetch(
        `${API_URL}/api/reports/${encodeURIComponent(caseNumber)}/files/${encodeURIComponent(filename)}/download`,
        { headers: { ...getAuthHeaders() } },
      );
      if (!response.ok) {
        let message = 'Download failed';
        try {
          message = (await response.json()).error || message;
        } catch { /* non-JSON error body */ }
        throw new Error(message);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to download report.');
    } finally {
      setDownloadingReport(null);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const statusLabel = (status?: string) => {
    switch (status) {
      case 'running': return 'Running Analysis';
      case 'awaiting_review': return 'Awaiting Review';
      case 'completed': return 'Completed';
      case 'failed': return 'Analysis Failed';
      default: return 'Active';
    }
  };

  if (loadingInitial) {
    return (
      <div className="case-details-page">
        <Navigation />
        <div className="case-details-loading-wrap">
          <div className="loading-spinner"></div>
          <h2>Loading Case Details...</h2>
          <p>Retrieving case metadata, evidence, and report data</p>
        </div>
      </div>
    );
  }

  if (initialError) {
    return (
      <div className="case-details-page">
        <Navigation />
        <div className="case-details-error-wrap">
          <div className="error-card">
            <i className="fas fa-exclamation-circle error-icon"></i>
            <h2>Unable to Load Case</h2>
            <p>{initialError === 'session_expired' ? 'Your session expired. Please log in again.' : initialError}</p>
            <div className="error-actions">
              <button onClick={() => navigate('/cases')} className="btn-secondary">
                <i className="fas fa-arrow-left"></i> Back to Cases
              </button>
              {initialError === 'session_expired' ? (
                <button onClick={() => navigate('/login')} className="btn-primary">
                  <i className="fas fa-sign-in-alt"></i> Log In
                </button>
              ) : (
                <button onClick={fetchCaseOverview} className="btn-primary">
                  <i className="fas fa-redo"></i> Retry
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!caseData) return null;

  return (
    <div className="case-details-page">
      <Navigation />

      <div className="case-details-container">
        {/* Top Breadcrumb & Actions Bar */}
        <div className="case-details-header">
          <div className="header-left">
            <button onClick={() => navigate('/cases')} className="back-btn" aria-label="Back to cases">
              <i className="fas fa-arrow-left"></i> Back
            </button>
            <div className="case-title-group">
              <h1 className="case-title">{caseData.case_number}</h1>
              <span className={`status-pill status-${caseData.status || 'active'}`}>
                {statusLabel(caseData.status)}
              </span>
            </div>
          </div>

          <div className="header-right">
            {caseData.status === 'awaiting_review' && (
              <button
                onClick={() => navigate('/analysis/review', { state: { draftCaseNumber: caseData.case_number } })}
                className="btn-review"
              >
                <i className="fas fa-edit"></i> Review & Finalize Draft
              </button>
            )}
            {caseData.status === 'completed' && (
              <button
                onClick={() => downloadReport(`${caseData.case_number}_report.docx`)}
                className="btn-download"
                disabled={downloadingReport !== null}
              >
                <i className="fas fa-download"></i>
                {downloadingReport ? 'Downloading…' : 'Download Final Report'}
              </button>
            )}
          </div>
        </div>

        {/* Section 1: Case Overview Card */}
        <div className="case-overview-card">
          <div className="overview-header">
            <h3><i className="fas fa-info-circle"></i> Case Summary & Metadata</h3>
            <span className="overview-created">Created: {formatDate(caseData.created_at)}</span>
          </div>

          <div className="overview-grid">
            <div className="overview-item">
              <label>Policy Number</label>
              <p>{caseData.policy_number?.trim() || '—'}</p>
            </div>
            <div className="overview-item">
              <label>Project Number</label>
              <p>{caseData.project_number?.trim() || '—'}</p>
            </div>
            <div className="overview-item">
              <label>Address</label>
              <p className="address-text">{caseData.address || '—'}</p>
            </div>
            <div className="overview-item">
              <label>Incident Date Range</label>
              <p>
                {caseData.date_start ? formatDate(caseData.date_start) : '—'}
                {caseData.date_end ? ` to ${formatDate(caseData.date_end)}` : ''}
              </p>
            </div>
            <div className="overview-item">
              <label>Structure Details</label>
              <p className="capitalize">
                {caseData.structure_type || 'Single Story'} ({caseData.total_floors || 1} floor{caseData.total_floors === 1 ? '' : 's'})
              </p>
            </div>
            <div className="overview-item">
              <label>Facing Direction</label>
              <p className="capitalize">{caseData.direction || '—'}</p>
            </div>
          </div>

          {caseData.notes && (
            <div className="overview-notes">
              <label>Engineer Notes</label>
              <p>{caseData.notes}</p>
            </div>
          )}
        </div>

        {/* Sticky Navigation & Filter Header */}
        <div className="case-details-sticky-header">
          <div className="case-tabs-bar" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'evidence'}
              className={`case-tab-btn ${activeTab === 'evidence' ? 'active' : ''}`}
              onClick={() => setActiveTab('evidence')}
            >
              <i className="fas fa-images"></i> Evidence & Media
              <span className="tab-badge">{(counts.images || 0) + (counts.svgs || 0) + (counts.audio_files || 0) + (counts.weather_documents || 0)}</span>
            </button>

            <button
              role="tab"
              aria-selected={activeTab === 'weather'}
              className={`case-tab-btn ${activeTab === 'weather' ? 'active' : ''}`}
              onClick={() => setActiveTab('weather')}
            >
              <i className="fas fa-wind"></i> Wind & Weather Data
              <span className="tab-badge">{counts.weather_data || 0}</span>
            </button>

            <button
              role="tab"
              aria-selected={activeTab === 'analysis'}
              className={`case-tab-btn ${activeTab === 'analysis' ? 'active' : ''}`}
              onClick={() => setActiveTab('analysis')}
            >
              <i className="fas fa-brain"></i> AI Vision Findings
              <span className="tab-badge">{counts.ai_analyses || 0}</span>
            </button>

            <button
              role="tab"
              aria-selected={activeTab === 'reports'}
              className={`case-tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              <i className="fas fa-file-word"></i> Reports & Drafts
              <span className="tab-badge">{reportItems.length || (reviewState?.draft_available ? 1 : 0)}</span>
            </button>

            <button
              role="tab"
              aria-selected={activeTab === 'logs'}
              className={`case-tab-btn ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              <i className="fas fa-history"></i> Audit Trail
              <span className="tab-badge">{counts.logs || 0}</span>
            </button>
          </div>

          {/* Evidence Sub-Filter Pills */}
          {activeTab === 'evidence' && (
            <div className="evidence-filters-bar">
              <button
                className={`filter-pill ${evidenceFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleEvidenceFilterChange('all')}
              >
                All Evidence
              </button>
              <button
                className={`filter-pill ${evidenceFilter === 'photograph' ? 'active' : ''}`}
                onClick={() => handleEvidenceFilterChange('photograph')}
              >
                <i className="fas fa-camera"></i> Photos ({counts.images || 0})
              </button>
              <button
                className={`filter-pill ${evidenceFilter === 'svg' ? 'active' : ''}`}
                onClick={() => handleEvidenceFilterChange('svg')}
              >
                <i className="fas fa-pen-nib"></i> Sketches ({counts.svgs || 0})
              </button>
              <button
                className={`filter-pill ${evidenceFilter === 'audio' ? 'active' : ''}`}
                onClick={() => handleEvidenceFilterChange('audio')}
              >
                <i className="fas fa-microphone"></i> Audio Memos ({counts.audio_files || 0})
              </button>
              <button
                className={`filter-pill ${evidenceFilter === 'weather_document' ? 'active' : ''}`}
                onClick={() => handleEvidenceFilterChange('weather_document')}
              >
                <i className="fas fa-file-pdf"></i> Wind PDFs ({counts.weather_documents || 0})
              </button>
            </div>
          )}
        </div>

        {/* Tab Content Container */}
        <div className="tab-content-container">
          {/* TAB 1: Evidence & Assets */}
          {activeTab === 'evidence' && (
            <div className="tab-pane evidence-tab">

              {/* Evidence Grid */}
              {evidenceItems.length === 0 && !evidenceLoading ? (
                <div className="empty-tab-state">
                  <i className="fas fa-folder-open empty-icon"></i>
                  <h3>No evidence recorded</h3>
                  <p>No media files were uploaded for this filter.</p>
                </div>
              ) : (
                <div className="evidence-grid">
                  {evidenceItems.map((item, idx) => (
                    <div key={`${item.id}-${idx}`} className="evidence-card">
                      {item.media_type === 'image' && (
                        <div className="media-preview image-preview" onClick={() => setSelectedPreviewImage(item)}>
                          <AuthImage
                            src={`${API_URL}${item.url}`}
                            alt={item.caption || item.filename}
                            loading="lazy"
                          />
                        </div>
                      )}

                      {item.media_type === 'svg' && (
                        <div className="media-preview svg-preview" onClick={() => setSelectedPreviewImage(item)}>
                          {item.svg_data ? (
                            <div className="inline-svg-wrap" dangerouslySetInnerHTML={{ __html: item.svg_data }} />
                          ) : (
                            <AuthImage src={`${API_URL}${item.url}`} alt={item.filename} />
                          )}
                          <span className="media-tag"><i className="fas fa-pen-nib"></i> SVG Drawing</span>
                        </div>
                      )}

                      {item.media_type === 'audio' && (
                        <div className="media-preview audio-card-preview">
                          <div className="audio-icon-banner">
                            <i className="fas fa-headphones-alt"></i>
                            <span>Voice Memo</span>
                          </div>
                          <AuthAudio src={`${API_URL}${item.url}`} />
                          {item.transcript && (
                            <details className="audio-transcript-accordion">
                              <summary>View Transcript</summary>
                              <p>{item.transcript}</p>
                            </details>
                          )}
                        </div>
                      )}

                      {item.media_type === 'weather_document' && (
                        <div className="media-preview doc-preview">
                          <div className="doc-icon-banner">
                            <i className="fas fa-file-pdf"></i>
                            <span>Wind Report PDF</span>
                          </div>
                          <div className="doc-info">
                            <span className="doc-size">{formatFileSize(item.file_size)}</span>
                            {item.summary && <p className="doc-summary">{item.summary}</p>}
                          </div>
                          <button
                            type="button"
                            className="doc-open-btn"
                            onClick={() => openAuthPdf(`${API_URL}${item.url}`, item.filename)}
                          >
                            <i className="fas fa-external-link-alt"></i> View PDF
                          </button>
                        </div>
                      )}

                      <div className="evidence-info">
                        <h4 className="evidence-filename" title={item.filename}>{item.filename}</h4>
                        {item.caption && <p className="evidence-caption">{item.caption}</p>}
                        {item.floor_level !== undefined && item.floor_level !== null && (
                          <span className="evidence-floor-badge">Floor {item.floor_level}</span>
                        )}
                        <span className="evidence-date">{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 2: Wind & Weather Data */}
          {activeTab === 'weather' && (
            <div className="tab-pane weather-tab">
              {weatherItems.length === 0 && !weatherLoading ? (
                <div className="empty-tab-state">
                  <i className="fas fa-cloud-sun empty-icon"></i>
                  <h3>No historical weather points recorded</h3>
                  <p>Weather verification did not produce discrete daily metrics for this case.</p>
                </div>
              ) : (
                <div className="weather-cards-grid">
                  {weatherItems.map((w, idx) => (
                    <div key={`${w.id}-${idx}`} className="weather-data-card">
                      <div className="weather-card-header">
                        <span className="weather-date-pill"><i className="fas fa-calendar-day"></i> {formatDate(w.weather_date)}</span>
                        {w.weather_json?.description && (
                          <span className="weather-desc-badge">{w.weather_json.description}</span>
                        )}
                      </div>

                      <div className="weather-metrics-grid">
                        <div className="metric-box">
                          <label><i className="fas fa-wind"></i> Wind Speed</label>
                          <span>{w.weather_json?.wind_speed ?? '—'} mph</span>
                        </div>
                        <div className="metric-box">
                          <label><i className="fas fa-compass"></i> Direction</label>
                          <span>{w.weather_json?.wind_direction ?? '—'}</span>
                        </div>
                        <div className="metric-box">
                          <label><i className="fas fa-temperature-high"></i> Temperature</label>
                          <span>{w.weather_json?.temperature ?? '—'} °F</span>
                        </div>
                        <div className="metric-box">
                          <label><i className="fas fa-cloud-rain"></i> Precipitation</label>
                          <span>{w.weather_json?.precipitation ?? '—'} in</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: AI Vision Findings */}
          {activeTab === 'analysis' && (
            <div className="tab-pane analysis-tab">
              {analysisItems.length === 0 && !analysisLoading ? (
                <div className="empty-tab-state">
                  <i className="fas fa-robot empty-icon"></i>
                  <h3>No AI analysis records found</h3>
                  <p>Analysis has not run or no damage findings were flagged.</p>
                </div>
              ) : (
                <div className="analysis-cards-grid">
                  {analysisItems.map((ai, idx) => (
                    <div key={`${ai.id}-${idx}`} className="analysis-card">
                      <div className="analysis-card-header">
                        <span className="analysis-badge"><i className="fas fa-microchip"></i> Vision Evaluation</span>
                        <span className="analysis-date">{formatDate(ai.created_at)}</span>
                      </div>
                      <div className="analysis-body">
                        {ai.short_description ? (
                          <p className="analysis-summary">{ai.short_description}</p>
                        ) : (
                          <p className="analysis-summary-muted">Evaluation output available</p>
                        )}

                        {ai.analysis_json && (
                          <details className="analysis-json-details">
                            <summary>Structured Findings</summary>
                            <pre>{JSON.stringify(ai.analysis_json, null, 2)}</pre>
                          </details>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: Reports & Drafts */}
          {activeTab === 'reports' && (
            <div className="tab-pane reports-tab">
              {reviewState?.draft_available && (
                <div className="review-banner-card">
                  <div className="banner-left">
                    <i className="fas fa-file-alt banner-icon"></i>
                    <div>
                      <h4>Active Draft Ready for Review</h4>
                      <p>An AI analysis draft has been generated and is awaiting your review and finalization.</p>
                      {reviewState.draft_updated_at && (
                        <span className="banner-timestamp">Draft updated: {formatDate(reviewState.draft_updated_at)}</span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/analysis/review', { state: { draftCaseNumber: caseData.case_number } })}
                    className="btn-primary"
                  >
                    <i className="fas fa-edit"></i> Open Draft Review
                  </button>
                </div>
              )}

              {reportItems.length > 0 ? (
                <div className="reports-grid">
                  {reportItems.map((report, idx) => (
                    <div key={`${report.id}-${idx}`} className="report-card">
                      <div className="report-icon-col">
                        <i className="fas fa-file-word"></i>
                      </div>
                      <div className="report-info-col">
                        <h4>{report.filename}</h4>
                        <span className="report-date">Generated: {formatDate(report.created_at)}</span>
                      </div>
                      <div className="report-action-col">
                        <button
                          onClick={() => downloadReport(report.filename)}
                          className="btn-download-report"
                          disabled={downloadingReport === report.filename}
                        >
                          <i className="fas fa-download"></i>
                          {downloadingReport === report.filename ? 'Downloading…' : 'Download DOCX'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !reviewState?.draft_available && (
                  <div className="empty-tab-state">
                    <i className="fas fa-file-alt empty-icon"></i>
                    <h3>No reports generated yet</h3>
                    <p>When an investigation is finalized, generated DOCX reports will appear here.</p>
                  </div>
                )
              )}
            </div>
          )}

          {/* TAB 5: Audit Trail */}
          {activeTab === 'logs' && (
            <div className="tab-pane logs-tab">
              {logItems.length === 0 && !logLoading ? (
                <div className="empty-tab-state">
                  <i className="fas fa-history empty-icon"></i>
                  <h3>No audit history found</h3>
                  <p>System activity events will be recorded here.</p>
                </div>
              ) : (
                <div className="audit-timeline">
                  {logItems.map((log, idx) => (
                    <div key={`${log.id}-${idx}`} className="timeline-item">
                      <div className="timeline-dot"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="timeline-stage">{log.stage || 'Event'}</span>
                          <span className="timeline-time">{formatDate(log.created_at)}</span>
                        </div>
                        {log.input && <p className="timeline-input">{log.input}</p>}
                        {log.output && <p className="timeline-output">{log.output}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Infinite Scroll Sentinel & Loading Indicator */}
          {((activeTab === 'evidence' && evidenceHasMore) ||
            (activeTab === 'weather' && weatherHasMore) ||
            (activeTab === 'analysis' && analysisHasMore) ||
            (activeTab === 'logs' && logHasMore)) && (
            <div ref={sentinelRef} className="infinite-scroll-sentinel">
              {(evidenceLoading || weatherLoading || analysisLoading || logLoading) && (
                <div className="loading-more-indicator">
                  <div className="mini-spinner"></div>
                  <span>Loading more items...</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox Modal for Photos & SVGs */}
      {selectedPreviewImage && (
        <div className="lightbox-overlay" onClick={() => setSelectedPreviewImage(null)}>
          <div className="lightbox-dialog" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close-btn" onClick={() => setSelectedPreviewImage(null)} aria-label="Close preview">
              <i className="fas fa-times"></i>
            </button>
            <div className="lightbox-media-wrap">
              {selectedPreviewImage.media_type === 'image' && (
                <AuthImage
                  src={`${API_URL}${selectedPreviewImage.url}`}
                  alt={selectedPreviewImage.caption || selectedPreviewImage.filename}
                />
              )}
              {selectedPreviewImage.media_type === 'svg' && selectedPreviewImage.svg_data && (
                <div className="lightbox-svg-render" dangerouslySetInnerHTML={{ __html: selectedPreviewImage.svg_data }} />
              )}
            </div>
            <div className="lightbox-footer">
              <h3>{selectedPreviewImage.filename}</h3>
              {selectedPreviewImage.caption && <p>{selectedPreviewImage.caption}</p>}
              {selectedPreviewImage.floor_level && <span>Floor Level: {selectedPreviewImage.floor_level}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDetails;
